import Box from "@mui/material/Box";
import { useTranslation } from "react-i18next";
import useSelectedHospitalValue from "../../hospitalModule/hooks/useSelectedHospitalValue";
import useAuthValue from "../../authModule/hooks/useAuthValue";
import { useMemo, useRef, useState } from "react";
import useFilterValue from "../hooks/useFilterValue";
import { buildQueryStringFromFilters } from "../../../helpers/buildQueryStringFromFilters";
//@ts-ignore
import { useScreenshot } from "use-react-screenshot";
import BarGraphLoader from "../../../components/skeletonLoaders/BarGraphLoader";
import Grid from "@mui/material/Grid";
import ChartContainer, {
  CHART_TYPE,
  RenderChartProps,
} from "../../../components/ChartContainer";
import BarChart from "../../../components/graphs/BarChart";
import useGetCasesPerNurseQuery from "../hooks/useGetCasesPerNurse";
import useGetVoiceNoteLoggedPerCaseByNurseQuery from "../hooks/useGetVoiceNoteLoggedPerCaseByNurse";
import useGetNurseOnTimeStartEndQuery from "../hooks/useGetNurseOnTimeStartEndQuery";
import useGetNurseAvgCasesPerActiveDayQuery from "../hooks/useGetNurseAvgCasesPerActiveDayQuery";
import TableChart from "../../../components/graphs/TableChart";
import { TABLE_HEADER_ARRAY } from "../../../utils/Types";
import { ON_TIME_STARTS_ENDS_FOR_NURSE_RESPONSE } from "../types/ResponseTypes";
import { OTOnTimeStartEndRow } from "./SurgeonAnalysis";
import useDashboardExport from "../hooks/useDashboardExport";
import useStableChartData from "../../../hooks/useStableChartData";

interface CirculatingNurseAnalysisProps {
  exportRoleBaesdScreenshot?: any;
}

const useCirculatingNurseData = (
  tenantId?: string,
  hospitalId?: string,
  queryString?: string
) => {
  const { data: { data: casesPerNurseData } = {}, isLoading: isGettingCasesPerNurse } =
    useGetCasesPerNurseQuery(tenantId, hospitalId, queryString);

  const {
    data: { data: nurseAvgCasesPerActiveDayData } = {},
    isLoading: isGettingNurseAvgCasesPerActiveDay,
  } = useGetNurseAvgCasesPerActiveDayQuery(tenantId, hospitalId, queryString);

  const {
    data: { data: onTimeStartsEndsForNursesData } = {},
    isLoading: isGettingOnTimeStartsEndsForNurses,
  } = useGetNurseOnTimeStartEndQuery(tenantId, hospitalId, queryString);

  const {
    data: { data: voiceNoteLoggedPerCaseByNurseData } = {},
    isLoading: isGettingVoiceNoteLoggedPerCaseByNurse,
  } = useGetVoiceNoteLoggedPerCaseByNurseQuery(
    tenantId,
    hospitalId,
    queryString
  );

  return {
    casesPerNurseData,
    isGettingCasesPerNurse,
    voiceNoteLoggedPerCaseByNurseData,
    isGettingVoiceNoteLoggedPerCaseByNurse,
    onTimeStartsEndsForNursesData,
    isGettingOnTimeStartsEndsForNurses,
    nurseAvgCasesPerActiveDayData,
    isGettingNurseAvgCasesPerActiveDay,
  };
};

const otOnTimeStartEndHeaders: TABLE_HEADER_ARRAY = [
  { key: "surgeonName", label: "Circulating Nurse",position:"center" },
  { key: "totalCases", label: "Total Cases", position: "center" },
  { key: "percentOnTimeStarts", label: "% On-Time Starts", position: "center" },
  { key: "percentOnTimeEnds", label: "% On-Time Ends", position: "center" },
];

// Helper to map API response to table rows
function mapOnTimeStartEndRows(
  data: Array<ON_TIME_STARTS_ENDS_FOR_NURSE_RESPONSE>
): OTOnTimeStartEndRow[] {
  return (data || []).map((row) => ({
    surgeonName: row.nurseName,
    totalCases: row.totalCases,
    percentOnTimeStarts: `${row.onTimeStartsPercentage?.toFixed(0)}%`,
    percentOnTimeEnds: `${row.onTimeEndsPercentage?.toFixed(0)}%`,
  }));
}

const CirculatingNurseAnalysis: React.FC<CirculatingNurseAnalysisProps> = ({
  exportRoleBaesdScreenshot,
}) => {
  const { t } = useTranslation();
  const currentHospitalDetails = useSelectedHospitalValue();
  const { tenantId: authTenantId, hospitalId: authHospitalId } = useAuthValue();
  const tenantId = currentHospitalDetails?.tenantId ?? authTenantId;
  const hospitalId = currentHospitalDetails?.uuid ?? authHospitalId;
  const screenshotRef = useRef(null);
  const [isExporting, setIsExporting] = useState(false);
  const filters = useFilterValue();
  const queryString = buildQueryStringFromFilters(filters);
  const [, takeScreenshot] = useScreenshot();

  const {
    isGettingCasesPerNurse,
    casesPerNurseData,
    isGettingNurseAvgCasesPerActiveDay,
    nurseAvgCasesPerActiveDayData,
    isGettingOnTimeStartsEndsForNurses,
    onTimeStartsEndsForNursesData,
    voiceNoteLoggedPerCaseByNurseData,
    isGettingVoiceNoteLoggedPerCaseByNurse,
  } = useCirculatingNurseData(tenantId, hospitalId, queryString);

  useDashboardExport({
    exportRef: exportRoleBaesdScreenshot,
    screenshotRef,
    setIsExporting,
    takeScreenshot,
    filename: "orva-circulating-nurse-analysis.pdf",
  });

  const casesPerNurseChartData = useMemo(() => {
    if (!casesPerNurseData) {
      return [];
    }
    return casesPerNurseData.map((item: any) => ({
      label: item.nurseName,
      value: item.totalCases,
    }));
  }, [casesPerNurseData]);

  // Falls back to the last non-empty result for this hospital so a transient
  // empty refetch doesn't flash "No graph to display" until the next poll.
  const nurseAvgCasesPerActiveDayChartData = useStableChartData(
    nurseAvgCasesPerActiveDayData,
    (item: any) => ({
      label: item.nurseName,
      value: Number(Number(item.avgCasesPerDay).toFixed(2)),
    }),
    hospitalId
  );

  const otOnTimeStartEndRows = useMemo(
    () => mapOnTimeStartEndRows(onTimeStartsEndsForNursesData ?? []),
    [onTimeStartsEndsForNursesData]
  );

  const voiceNoteLoggedPerCaseByNurseChartData = useMemo(() => {
    if (!voiceNoteLoggedPerCaseByNurseData) {
      return [];
    }
    return voiceNoteLoggedPerCaseByNurseData?.map((item: any) => ({
      label: item?.nurseName,
      value: item?.averageVoiceNotesPerCase,
    }));
  }, [voiceNoteLoggedPerCaseByNurseData]);

  const mappedChartArr = [
    {
      chartLabel: "Circulating_Nurse_Chart1",
      chartData: casesPerNurseChartData,
      isLoading: isGettingCasesPerNurse,
      tooltipTitle: "Circulating_Nurse_Chart1_Tooltip",
    },
    {
      chartLabel: "Circulating_Nurse_Chart2",
      chartData: nurseAvgCasesPerActiveDayChartData,
      isLoading: isGettingNurseAvgCasesPerActiveDay,
      tooltipTitle: "Circulating_Nurse_Chart2_Tooltip",
    },
    {
      chartLabel: "Circulating_Nurse_Chart3",
      chartData: {
        headers: otOnTimeStartEndHeaders,
        rowData: otOnTimeStartEndRows,
      },
      isLoading: isGettingOnTimeStartsEndsForNurses,
      chartType: CHART_TYPE.TABLE,
      tooltipTitle: "Circulating_Nurse_Chart3_Tooltip",
    },
    {
      chartLabel: "Circulating_Nurse_Chart4",
      chartData: voiceNoteLoggedPerCaseByNurseChartData,
      isLoading: isGettingVoiceNoteLoggedPerCaseByNurse,
      tooltipTitle: "Circulating_Nurse_Chart4_Tooltip",
    },
  ];

  const renderBarChart = ({
    data,
    title,
    tooltipTitle,
    isLoading,
    props,
    chartType,
  }: RenderChartProps) => {
    if (isLoading && chartType !== CHART_TYPE.TABLE) {
      return <BarGraphLoader {...props} />;
    }

    if (chartType === CHART_TYPE.TABLE) {
      return (
        <TableChart
          headers={Array.isArray(data) ? [] : data?.headers}
          rowData={Array.isArray(data) ? [] : data?.rowData}
          title={title}
          tooltipTitle={tooltipTitle}
          isLoading={isLoading}
          {...props}
        />
      );
    }

    return (
      <BarChart
        key={hospitalId}
        title={t(title)}
        tooltipTitle={t(tooltipTitle)}
        data={data}
        {...props}
      />
    );
  };

  return (
    <Box ref={screenshotRef} flex={1} py={2} sx={{ px: isExporting ? 2 : 0 }}>
      <Grid container spacing={1.5}>
        {mappedChartArr.map((item) => (
          <Grid key={item.chartLabel} size={{ xs: 12, md: 6 }}>
            <ChartContainer
              isLoading={item.isLoading}
              data={item.chartData}
              title={item.chartLabel}
              tooltipTitle={item.tooltipTitle}
              chartType={item.chartType}
              props={{
                showLabels: false,
                showXAxis: true,
                categoryFontSize: 12,
                labelColNumber: 2,
                showAverageLine: true,
                showLabelInHeader: false,
              }}
              renderChart={renderBarChart}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default CirculatingNurseAnalysis;
